import { useRef, useState } from 'react';
import { supabase } from '../supabaseClient';
import './ImageUpload.css';

// ─── ImageUpload ──────────────────────────────────────────────────────────────
//
// Props:
//   folder      - storage path prefix, e.g. 'recipes/42' or 'recipes/new'
//   onUploaded  - called with the public URL string when upload succeeds
//   disabled    - optional, disables the button

const MAX_WIDTH = 1200;
const JPEG_QUALITY = 0.85;

function resizeImage(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      if (width > MAX_WIDTH || height > MAX_WIDTH) {
        if (width >= height) {
          height = Math.round((height / width) * MAX_WIDTH);
          width = MAX_WIDTH;
        } else {
          width = Math.round((width / height) * MAX_WIDTH);
          height = MAX_WIDTH;
        }
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        blob => blob ? resolve(blob) : reject(new Error('Canvas conversion failed')),
        'image/jpeg',
        JPEG_QUALITY
      );
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Could not read image')); };
    img.src = url;
  });
}

function ImageUpload({ folder, onUploaded, disabled }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  async function handleFileChange(e) {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;

    const allowed = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowed.includes(file.type)) {
      setError('Please use a JPEG, PNG, or WebP image.');
      return;
    }

    setError('');
    setUploading(true);

    try {
      const resized = await resizeImage(file);
      const path = `${folder}/${Date.now()}.jpg`;

      const { error: uploadErr } = await supabase.storage
        .from('recipe-images')
        .upload(path, resized, { upsert: true, contentType: 'image/jpeg' });

      if (uploadErr) throw new Error(uploadErr.message);

      const { data } = supabase.storage
        .from('recipe-images')
        .getPublicUrl(path);

      onUploaded(data.publicUrl);
    } catch (err) {
      setError(`Upload failed: ${err.message}`);
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="image-upload">
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />
      <button
        type="button"
        className="image-upload__btn"
        onClick={() => inputRef.current?.click()}
        disabled={disabled || uploading}
      >
        {uploading ? (
          <>
            <span className="image-upload__spinner" />
            Resizing &amp; uploading…
          </>
        ) : (
          <>📁 Upload from device</>
        )}
      </button>
      {error && <p className="image-upload__error">{error}</p>}
    </div>
  );
}

export default ImageUpload;
